import { AssetIcon } from '@/components/AssetIcon';
import { AssetDialog } from '@/components/dialogs/AssetDialog';
import { GuideMenu } from '@/components/GuideMenu';
import { Card } from '@/components/ui/card';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

const formatReleaseTick = (tick: number | string): string => {
  if (typeof tick === 'number') {
    if (tick === 0) return 'Available at start';
    if (tick === -1) return 'After protection period';
    return `Available at tick ${tick}`;
  }

  // Handle string formats
  if (tick === 'FREEZE_TIME') return 'Available at Age Start';
  if (tick === 'OOP') return 'OOP (After protection period)';
  if (tick === 'SPECIAL_TIME') return 'Special Time';
  if (tick.startsWith('WEEK_')) {
    const week = tick.replace('WEEK_', '');
    return `Available in Year ${week}`;
  }

  return String(tick);
};

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Understanding The Market
    </h3>
    <p className="text-[#b8b8b8]">
      There are many magic and special items that become available to your
      province.
    </p>
    <p className="text-[#b8b8b8]">
      You can gain items via The Arcane Market or using the auction. Click the
      icons on the table below to view the effect of the item.
    </p>
  </div>
);

const ItemsGrid = ({ items }: { items: any[] }) => {
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-[#e6c547]">Available Items</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item) => (
          <div
            key={item.assetCode}
            className="bg-white/[0.03] p-4 rounded-lg border border-white/10"
          >
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => setSelectedItem(item.assetCode)}
                className="hover:opacity-80 transition-opacity"
              >
                <AssetIcon
                  assetCode={item.assetCode}
                  assetType={item.assetTypeCode}
                  size="small"
                  className="rounded-full"
                />
              </button>
              <div>
                <h4 className="text-lg font-semibold text-[#f0d060]">
                  {item.name}
                </h4>
                <p className="text-sm text-[#9a9a9a] italic">
                  {formatReleaseTick(item.releaseTick)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedItem &&
        items.find((item) => item.assetCode === selectedItem) && (
          <AssetDialog
            isOpen={!!selectedItem}
            onClose={() => setSelectedItem(null)}
            asset={items.find((item) => item.assetCode === selectedItem)!}
          />
        )}
    </div>
  );
};

const MarketDetailsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">The Arcane Market</h3>
    <div className="space-y-4">
      <p className="text-[#b8b8b8]">
        The Arcane Market sells items directly to your kingdom, and auctions off
        new and rarer curious goods. Items will remain with your province until
        the end of the age, even in death and resurrection unless the items are
        discarded, used, or sold. Spurning gold, the mysterious sellers require
        payment only in gems which can be collected in various ways. Some
        special items can only be gained by collecting the a full set. Once you
        have the set of items in your equipped inventory, you are then provided
        with the option to exchange them all for a legendary item. There is a
        limit of one legendary item per province.
      </p>
    </div>

    <div className="mt-8">
      <h3 className="text-xl font-semibold text-[#e6c547] mb-4">
        Gem Collection
      </h3>
      <div className="bg-white/[0.03] p-6 rounded-lg border border-white/10">
        <div className="space-y-4">
          <p className="text-[#b8b8b8]">
            Gems are collected through game actions, such as:
          </p>
          <ul className="list-disc list-inside space-y-2 text-[#b8b8b8] pl-4">
            <li>Attacking</li>
            <li>Performing thievery actions</li>
            <li>Casting spells</li>
            <li>Achieving new ranks of honour</li>
            <li>Reaching certain acre landmarks</li>
            <li>Inviting your friends to play</li>
          </ul>
          <p className="text-[#b8b8b8]">
            Alternatively, you can also bolster your gem stocks by selling your
            Servants, which are detailed in the next section.
          </p>
          <div className="mt-4 space-y-2">
            <p className="text-[#b8b8b8]">
              <span className="text-[#f0d060]">Reward gems</span> differ from{' '}
              <span className="text-[#f0d060]">Permanent gems</span>, in that
              they also share the same fate as your province, and last only
              until end of age. Servants, and gems from sold servants, are
              permanent. They remain with your account from age to age until
              used.
            </p>
            <p className="text-[#b8b8b8]">
              When buying items from the market, your reward gems are used
              before any bought permanent gems. Any item sold back to the market
              will be paid for in reward gems.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div className="mt-6">
      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <p className="text-[#b8b8b8] italic">
          Every day, the merchants may add an item to your kingdom market, at a
          price previously set by the auction.
        </p>
      </div>
    </div>

    <div className="mt-8 space-y-4">
      <h3 className="text-xl font-semibold text-[#e6c547]">Use The Right Page</h3>
      <div className="grid gap-3 md:grid-cols-3">
        {[
          {
            to: '/auction',
            title: 'Auction',
            description: 'World bidding rules, time extensions, and sitting behavior.',
          },
          {
            to: '/gems',
            title: 'Servants & Gems',
            description: 'Permanent gems, selling servants, and account-level value.',
          },
          {
            to: '/trade',
            title: 'Trade & Aid',
            description: 'Moving resources or servants between kingdom mates.',
          },
        ].map((item) => (
          <Link key={item.to} to={item.to}>
            <Card className="h-full p-4 transition-colors hover:border-[#d4af37]/40">
              <h4 className="text-lg font-semibold text-[#f0d060]">
                {item.title}
              </h4>
              <p className="mt-2 text-sm text-[#b8b8b8]">{item.description}</p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  </div>
);

export const Route = createFileRoute('/market/')({
  component: MarketPage,
});

function MarketPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/market" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/misc/the_arcane_market@2x.png"
              alt="Market"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h2 className="text-2xl font-bold">The Market</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <ItemsGrid items={[]} />
            <MarketDetailsSection />
          </div>
        </section>
      </div>
    </div>
  );
}
